// FirewallUsers.tsx
import { useState, useCallback, useEffect } from "react";
import {
    RefreshCw, Loader2, Users,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, Search
} from "lucide-react";
import { showError } from "../../../../utils/toast";
import baseapi from "../../../../services/baseapi";


// ── Types ─────────────────────────────────────────────────────────────────────
interface FirewallUser {
    id: number;
    name: string;
    status: string;
    type: string;
    two_factor: string;
    authtimeout: number;
    passwd_policy: string;
    ldap_server: string;
    radius_server: string;
    email_to: string;
}

// ── Value Chip ────────────────────────────────────────────────────────────────
const ValChip = ({ val }: { val: string | undefined }) => {
    const str = String(val ?? "");
    if (!str) return <span className="text-gray-200 text-[9px]">—</span>;
    const lower = str.toLowerCase();
    const style =
        lower === "enable"
            ? "bg-green-500/10 border-green-500/20 text-green-400"
            : lower === "disable"
                ? "bg-gray-700/40 border-gray-600/30 text-gray-200"
                : lower === "password"
                    ? "bg-blue-500/10 border-blue-500/20 text-blue-400"
                    : lower === "ldap"
                        ? "bg-violet-500/10 border-violet-500/20 text-violet-400"
                        : lower === "radius"
                            ? "bg-cyan-500/10 border-cyan-500/20 text-cyan-400"
                            : "bg-indigo-500/10 border-indigo-500/20 text-indigo-400";

    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold border uppercase whitespace-nowrap ${style}`}>
            {str}
        </span>
    );
};

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ col, sort }: { col: string; sort: { key: string; dir: "asc" | "desc" } }) => {
    if (sort.key !== col)
        return <ArrowUp size={9} className="opacity-0 group-hover:opacity-30 text-gray-200 transition-opacity" />;
    return sort.dir === "asc"
        ? <ArrowUp size={9} className="text-blue-400" />
        : <ArrowDown size={9} className="text-blue-400" />;
};

// ── Columns ───────────────────────────────────────────────────────────────────
const COLS: { key: keyof FirewallUser; label: string; width: string; render: (u: FirewallUser) => React.ReactNode }[] = [
    {
        key: "name", label: "Name", width: "min-w-[140px]",
        render: u => (
            <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                    <span className="text-[9px] font-black text-blue-400">{u.name?.[0]?.toUpperCase()}</span>
                </div>
                <span className="text-[11px] font-bold text-white">{u.name}</span>
            </div>
        ),
    },
    {
        key: "status", label: "Status", width: "w-20",
        render: u => <ValChip val={u.status} />,
    },
    {
        key: "type", label: "Type", width: "w-24",
        render: u => <ValChip val={u.type} />,
    },
    {
        key: "two_factor", label: "2FA", width: "w-20",
        render: u => <ValChip val={u.two_factor} />,
    },
    {
        key: "authtimeout", label: "Auth Timeout", width: "w-28",
        render: u => (
            <span className="text-[11px] font-mono text-gray-300">
                {u.authtimeout === 0 ? <span className="text-gray-200">Default</span> : `${u.authtimeout} s`}
            </span>
        ),
    },
    {
        key: "passwd_policy", label: "Password Policy", width: "min-w-[140px]",
        render: u => (
            u.passwd_policy
                ? <span className="text-[11px] font-mono text-gray-300">{u.passwd_policy}</span>
                : <span className="text-gray-200 text-[10px]">—</span>
        ),
    },
];

// ── Page Size Options ─────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 20, 50];

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallUsers({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [users, setUsers] = useState<FirewallUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "name", dir: "asc" });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id: asset_id, client_id: client_id })
            }
            const res = await baseapi.get("/firewall_users", { params });
            if (res.data.status) {
                setUsers(res.data.data ?? []);
            } else showError("Failed to load firewall users");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { setPage(1); }, [search]);

    // Filter
    const filtered = users.filter(u => {
        if (!search) return true;
        const q = search.toLowerCase();
        return u.name.toLowerCase().includes(q) || u.type?.toLowerCase().includes(q);
    });

    // Sort
    const sorted = [...filtered].sort((a, b) => {
        const av = (a as any)[sort.key];
        const bv = (b as any)[sort.key];
        const cmp = typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av ?? "").localeCompare(String(bv ?? ""));
        return sort.dir === "asc" ? cmp : -cmp;
    });

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageItems = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
    const start = sorted.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const end = Math.min(safePage * pageSize, sorted.length);

    const handleSort = (key: string) => {
        setSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
        setPage(1);
    };

    return (
        <div className="flex flex-col gap-3">

            {/* Filter Bar */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative group">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-200 group-focus-within:text-blue-400 transition-colors" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search users..."
                        className="w-52 bg-gray-900 border border-gray-700 rounded-lg pl-7 pr-3 py-1.5 text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/50 transition-all"
                    />
                </div>
                {search && (
                    <button onClick={() => setSearch("")}
                        className="text-[9px] text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest">
                        Clear
                    </button>
                )}
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="ml-auto p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-blue-400"} />
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-20">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">Loading users...</span>
                </div>
            ) : sorted.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                    <Users size={22} className="text-gray-200" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No users found</span>
                </div>
            ) : (
                <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-700/60">
                                    {COLS.map(col => (
                                        <th
                                            key={col.key}
                                            onClick={() => handleSort(col.key)}
                                            className={`px-3 py-2 text-[9px] font-bold text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group select-none ${col.width}`}
                                        >
                                            <div className="flex items-center gap-1">
                                                {col.label}
                                                <SortIcon col={col.key} sort={sort} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/30">
                                {pageItems.map(user => (
                                    <tr key={user.id} className="hover:bg-gray-700/20 transition-colors">
                                        {COLS.map(col => (
                                            <td key={col.key} className="px-3 py-2.5 align-middle">
                                                {col.render(user)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-3 py-2 border-t border-gray-700/60 bg-gray-800/40 flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-gray-200 uppercase tracking-widest">Rows</span>
                                <select
                                    value={pageSize}
                                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    className="bg-gray-900 border border-gray-700 rounded px-1.5 py-0.5 text-[10px] text-gray-300 focus:outline-none"
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <span className="text-[9px] text-gray-200">
                                <span className="text-gray-300">{start}</span>{" – "}
                                <span className="text-gray-300">{end}</span>
                                <span className="mx-1 text-gray-200">/</span>
                                <span className="text-gray-300 font-bold">{sorted.length}</span>
                                {sorted.length !== users.length && (
                                    <span className="text-gray-200 ml-1">(filtered from {users.length})</span>
                                )}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button disabled={safePage === 1} onClick={() => setPage(1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronsLeft size={13} />
                            </button>
                            <button disabled={safePage === 1} onClick={() => setPage(p => p - 1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronLeft size={13} />
                            </button>
                            <span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded text-[10px] font-bold mx-1 min-w-[28px] text-center">
                                {safePage}
                            </span>
                            <span className="text-[9px] text-gray-200">/ {totalPages}</span>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronRight size={13} />
                            </button>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronsRight size={13} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
